import { zodResolver } from '@hookform/resolvers/zod';
import { TrashIcon } from '@phosphor-icons/react';
import { nanoid } from 'nanoid';
import { JSX, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import colors from 'tailwindcss/colors';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

import { EventParticipant } from '../types';
import { ParticipantTypeFormSchema } from '../validations';
import { RemoveParticipantTypeDialog } from './remove-participant-type-dialog';

const MAX_TYPES_COUNT = 50;

export type ParticipantTypeFormValues = z.infer<
  typeof ParticipantTypeFormSchema
>;

interface ParticipantTypeFormProps {
  children: () => JSX.Element;
  maxPhoneNumbersCount: number;
  participants?: EventParticipant[];
  defaultValues?: ParticipantTypeFormValues;
  onSubmit: (data: ParticipantTypeFormValues) => Promise<void>;
}

export const defaultFormValues = {
  types: [
    {
      value: '',
      id: nanoid(),
      phoneNumbers: [{ value: '' }],
    },
  ],
};

export function ParticipantTypeForm({
  onSubmit,
  children,
  defaultValues,
  participants,
  maxPhoneNumbersCount,
}: ParticipantTypeFormProps) {
  const [activeDeletionTypeId, setActiveDeletionTypeId] = useState(NaN);

  const form = useForm<ParticipantTypeFormValues>({
    resolver: zodResolver(ParticipantTypeFormSchema),
    defaultValues: defaultValues || defaultFormValues,
  });

  const fields = form.watch('types', []);
  const { append, remove, update } = useFieldArray({
    control: form.control,
    name: 'types',
  });

  const handleFormSubmit = async (data) => await onSubmit(data);

  const addPhoneNumber = (index: number) => {
    const previousValue = fields[index].phoneNumbers;

    update(index, {
      id: fields[index].id,
      value: fields[index].value || '',
      phoneNumbers: [...previousValue, { value: '' }],
    });
  };

  const addNewType = () => {
    append({ id: nanoid(), value: '', phoneNumbers: [{ value: '' }] });
  };

  const checkIfTypeIsLinkedToParticipants = (index: number) => {
    const participantType = fields[index].value;

    if (participantType) {
      const isLinkedToParticipants = participants?.some(
        (participant) =>
          participant.type?.toLowerCase() === participantType.toLowerCase()
      );

      if (isLinkedToParticipants) {
        setActiveDeletionTypeId(index);
      } else {
        remove(index);
      }
    }
  };

  const removeParticipantType = () => {
    remove(activeDeletionTypeId);
    setActiveDeletionTypeId(NaN);
  };

  return (
    <>
      <Form {...form}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit(handleFormSubmit)(event);
          }}
          className="py-6"
        >
          <ScrollArea className="h-[520px]">
            <div className="flex flex-col max-w-6xl">
              {fields.map((item, index) => {
                const phoneNumbers = fields[index].phoneNumbers;

                return (
                  <div className="mx-1" key={`types.${item.id}.value`}>
                    <h4 className="text-zinc-50 font-semibold mb-5">
                      Type {index + 1}
                    </h4>
                    <div className="grid grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name={`types.${index}.value`}
                        data-testid={`types.${item.id}.value`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Participant type</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Max 50 characters"
                                maxLength={50}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="flex gap-2">
                        <div className="flex flex-col gap-5">
                          {phoneNumbers.map((_, phoneNumberIndex) => (
                            <FormField
                              control={form.control}
                              key={`types.${item.id}.phoneNumbers.${phoneNumberIndex}.value`}
                              name={`types.${index}.phoneNumbers.${phoneNumberIndex}.value`}
                              data-testid={`types.${item.id}.phoneNumbers.${phoneNumberIndex}.value`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Phone number</FormLabel>
                                  <FormControl>
                                    <PhoneInput
                                      placeholder="Phone number"
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          ))}

                          {phoneNumbers.length < maxPhoneNumbersCount && (
                            <Button
                              variant="outline"
                              className="w-[130px]"
                              onClick={() => addPhoneNumber(index)}
                              data-testid="add-number-button"
                            >
                              + Add Number
                            </Button>
                          )}
                        </div>

                        {fields.length > 1 && (
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-11 w-11 mt-[32px]"
                            onClick={() => {
                              checkIfTypeIsLinkedToParticipants(index);
                            }}
                            data-testid="remove-type-button"
                          >
                            <TrashIcon size={28} color={colors.zinc[400]} />
                          </Button>
                        )}
                      </div>
                    </div>

                    {index !== fields.length - 1 && (
                      <Separator className="my-5" />
                    )}
                  </div>
                );
              })}

              {fields.length < MAX_TYPES_COUNT && (
                <div className="mx-1">
                  <Separator className="my-5" />

                  <div>
                    <h4 className="text-zinc-50 font-semibold mb-5">
                      Type {fields.length + 1}
                    </h4>
                    <Button
                      variant="outline"
                      className="w-[130px]"
                      onClick={addNewType}
                      data-testid="add-type-button"
                    >
                      + Add Type
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {children()}
        </form>
      </Form>

      {!Number.isNaN(activeDeletionTypeId) && (
        <RemoveParticipantTypeDialog
          isOpen
          onSubmit={removeParticipantType}
          type={fields?.[activeDeletionTypeId]?.value}
          onOpenChange={() => setActiveDeletionTypeId(NaN)}
        />
      )}
    </>
  );
}
